import { useEffect, useState } from "react";
import axios from "axios";
import {
  ExternalLink, Youtube, Play, Music, MapPin, Quote as QuoteIcon, MailCheck, Loader2,
} from "lucide-react";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";
import { API } from "@/App";
import { parseYouTubeId, getYouTubeThumbnail } from "@/lib/youtube";
import {
  parseSpotify, isTikTokUrl, isSoundCloudUrl, isMapsEmbedUrl,
  SOCIAL_PLATFORMS, getSocialPlatform,
} from "@/lib/blocks";

// ============ VIDEO ============

// Ambil thumbnail TikTok lewat oEmbed (publik, tanpa API key). Bila gagal
// (CORS/blokir jaringan) kembalikan null dan renderer memakai placeholder.
const useTikTokEmbed = (url) => {
  const [embed, setEmbed] = useState(null);
  useEffect(() => {
    let cancelled = false;
    setEmbed(null);
    if (!isTikTokUrl(url)) return;
    axios
      .get(`https://www.tiktok.com/oembed?url=${encodeURIComponent(url.trim())}`, { timeout: 6000 })
      .then((res) => {
        if (!cancelled) setEmbed({ thumb: res.data?.thumbnail_url, title: res.data?.title });
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [url]);
  return embed;
};

export const VideoCard = ({ block, index, color, radius, ink, subInk, isLight, interactive, onLinkClick }) => {
  const youtubeId = parseYouTubeId(block.url);
  const tiktok = useTikTokEmbed(youtubeId ? "" : block.url);
  const thumb = youtubeId ? getYouTubeThumbnail(youtubeId) : tiktok?.thumb;
  const title = (block.title || tiktok?.title || "").trim();
  const platform = youtubeId ? "YouTube" : isTikTokUrl(block.url) ? "TikTok" : "Video";
  const cardBg = isLight ? "#ffffff" : "rgba(255,255,255,.06)";
  const cardBorder = isLight ? "rgba(0,0,0,.08)" : "rgba(255,255,255,.12)";
  const iconColor = platform === "TikTok" ? "#000000" : "#ff0000";
  const iconBg = platform === "TikTok" ? "#ffffff" : "#ff0000";
  const iconText = platform === "TikTok" ? "#111" : "#fff";

  const content = (
    <div
      className="overflow-hidden transition-transform hover:scale-[1.02] hover:-translate-y-0.5"
      style={{ borderRadius: radius, background: cardBg, border: `1px solid ${cardBorder}` }}
    >
      <div className="relative w-full aspect-video overflow-hidden" style={{ background: "#0f0f0f" }}>
        {thumb ? (
          <img src={thumb} alt={title || "Video"} className="w-full h-full object-cover" loading="lazy" referrerPolicy="no-referrer" />
        ) : (
          <div className="w-full h-full grid place-items-center" style={{ background: `${color}2e` }}>
            {platform === "TikTok" ? <span className="text-2xl">🎵</span> : <Youtube className="w-10 h-10" style={{ color }} />}
          </div>
        )}
        <div className="absolute inset-0 grid place-items-center bg-black/30">
          <span
            className="w-14 h-14 rounded-full grid place-items-center transition-transform group-hover:scale-110"
            style={{ background: iconBg, boxShadow: `0 10px 26px ${platform === "TikTok" ? "rgba(0,0,0,.4)" : "rgba(255,0,0,.45)"}` }}
          >
            <Play className="w-6 h-6 translate-x-[1px]" style={{ color: iconText }} fill="currentColor" strokeWidth={0} />
          </span>
        </div>
      </div>
      <div className="flex items-center gap-2.5 px-3.5 py-3">
        <span className="w-8 h-8 rounded-lg grid place-items-center shrink-0" style={{ background: iconBg, color: iconText }}>
          {platform === "TikTok" ? <span className="text-sm">♪</span> : <Youtube className="w-4 h-4" />}
        </span>
        <span className="flex-1 min-w-0 text-left">
          <span className="block truncate font-bold" style={{ fontSize: 13, color: ink }}>{title || "Tonton video"}</span>
          <span className="block text-[10.5px] opacity-60" style={{ color: subInk }}>{platform}</span>
        </span>
      </div>
    </div>
  );

  if (!interactive) return <div className="group">{content}</div>;
  return (
    <a href={block.url} target="_blank" rel="noopener noreferrer" onClick={() => onLinkClick?.(block, index)}
      className="block group" data-testid={`microsite-link-${index}`}>
      {content}
    </a>
  );
};

// ============ AUDIO ============

const AudioCard = ({ block, color, radius, ink, subInk, isLight, interactive, onLinkClick, index }) => {
  const spotify = parseSpotify(block.url);
  const soundcloud = isSoundCloudUrl(block.url);
  const title = (block.title || "").trim();
  const cardBg = isLight ? "#ffffff" : "rgba(255,255,255,.06)";
  const cardBorder = isLight ? "rgba(0,0,0,.08)" : "rgba(255,255,255,.12)";
  const embedUrl = spotify
    ? `https://open.spotify.com/embed/${spotify.type}/${spotify.id}`
    : soundcloud
      ? `https://w.soundcloud.com/player/?url=${encodeURIComponent(block.url.trim())}&color=%237c3aed&visual=true`
      : null;

  if (interactive && embedUrl) {
    return (
      <div style={{ borderRadius: radius, overflow: "hidden", border: `1px solid ${cardBorder}`, background: cardBg }}>
        {title && <p className="px-3.5 pt-3 font-bold" style={{ fontSize: 13, color: ink }}>{title}</p>}
        <iframe
          src={embedUrl}
          title={title || "Audio"}
          className="w-full"
          style={{ height: spotify?.type === "track" ? 80 : 152, border: 0 }}
          loading="lazy"
          allow="encrypted-media"
        />
      </div>
    );
  }

  const content = (
    <div className="flex items-center gap-2.5 transition-transform hover:scale-[1.015]" style={{ background: cardBg, border: `1px solid ${cardBorder}`, borderRadius: radius, padding: "12px 14px" }}>
      <span className="w-9 h-9 rounded-lg grid place-items-center shrink-0" style={{ background: "#1db9541a", color: "#1db954" }}>
        <Music className="w-4 h-4" />
      </span>
      <span className="flex-1 min-w-0 text-left">
        <span className="block truncate font-bold" style={{ fontSize: 13, color: ink }}>{title || "Dengarkan musik"}</span>
        <span className="block text-[10.5px] opacity-60" style={{ color: subInk }}>{spotify ? "Spotify" : soundcloud ? "SoundCloud" : "Audio"}</span>
      </span>
      <ExternalLink className="w-3.5 h-3.5 shrink-0 opacity-50" />
    </div>
  );

  if (!interactive) return <div className="group">{content}</div>;
  return (
    <a href={block.url} target="_blank" rel="noopener noreferrer" onClick={() => onLinkClick?.(block, index)} className="block group" data-testid={`microsite-link-${index}`}>
      {content}
    </a>
  );
};

// ============ IMAGE / GALLERY ============

const ImageBlock = ({ block, radius, ink, subInk, isLight, interactive, onLinkClick, index }) => {
  const img = (
    <div className="w-full" style={{ borderRadius: radius, overflow: "hidden", border: `1px solid ${isLight ? "rgba(0,0,0,.08)" : "rgba(255,255,255,.12)"}` }}>
      <img src={block.url} alt={block.caption || "Gambar"} className="w-full object-cover" style={{ maxHeight: 420 }} loading="lazy" />
    </div>
  );
  const caption = (block.caption || "").trim();
  const withCaption = caption ? (
    <div className="space-y-1.5">
      {img}
      {caption && <p className="text-center text-[11.5px] opacity-70" style={{ color: subInk }}>{caption}</p>}
    </div>
  ) : img;

  if (!interactive) return <div className="group">{withCaption}</div>;

  const inner = block.link_url ? (
    <a href={block.link_url} target="_blank" rel="noopener noreferrer" onClick={() => onLinkClick?.(block, index)} className="block group">
      {withCaption}
    </a>
  ) : withCaption;

  return <div data-testid={`microsite-link-${index}`}>{inner}</div>;
};

const GalleryBlock = ({ block, radius, isLight }) => {
  const items = (block.items || []).filter((x) => x?.trim());
  if (items.length === 0) return null;
  const cols = items.length === 1 ? 1 : items.length === 2 ? 2 : 3;
  return (
    <div className="grid gap-2" style={{ gridTemplateColumns: `repeat(${cols}, 1fr)` }}>
      {items.map((src, i) => (
        <a key={i} href={src} target="_blank" rel="noopener noreferrer" className="block overflow-hidden"
          style={{ borderRadius: Math.max(radius - 6, 6), border: `1px solid ${isLight ? "rgba(0,0,0,.08)" : "rgba(255,255,255,.12)"}` }}>
          <img src={src} alt={`Galeri ${i + 1}`} className="w-full aspect-square object-cover" loading="lazy" />
        </a>
      ))}
    </div>
  );
};

// ============ TEXT / HEADING / DIVIDER / QUOTE ============

const TextBlock = ({ block, ink }) => (
  <p className="whitespace-pre-wrap leading-relaxed" style={{ fontSize: 13.5, color: ink, textAlign: block.style === "center" ? "center" : "left" }}>
    {block.content}
  </p>
);

const HeadingBlock = ({ block, ink }) => (
  <h2 className="font-extrabold leading-tight" style={{ fontSize: 18, color: ink, textAlign: block.style === "center" ? "center" : "left", letterSpacing: "-0.01em" }}>
    {block.title}
  </h2>
);

const DividerBlock = ({ block, ink, subInk, isLight }) => {
  if (!block.label?.trim()) {
    return <div style={{ borderTop: `1px solid ${isLight ? "rgba(0,0,0,.12)" : "rgba(255,255,255,.14)"}` }} />;
  }
  return (
    <div className="flex items-center gap-3">
      <div className="flex-1" style={{ borderTop: `1px solid ${isLight ? "rgba(0,0,0,.12)" : "rgba(255,255,255,.14)"}` }} />
      <span className="text-[11px] font-semibold uppercase tracking-wide" style={{ color: subInk }}>{block.label}</span>
      <div className="flex-1" style={{ borderTop: `1px solid ${isLight ? "rgba(0,0,0,.12)" : "rgba(255,255,255,.14)"}` }} />
    </div>
  );
};

const QuoteBlock = ({ block, color, ink, subInk, isLight }) => (
  <div className="relative px-4 py-3" style={{ borderLeft: `3px solid ${color}`, background: `${color}14`, borderRadius: 10 }}>
    <QuoteIcon className="w-5 h-5 mb-1.5 opacity-50" style={{ color }} />
    <p className="italic leading-relaxed" style={{ fontSize: 13.5, color: ink }}>“{block.content}”</p>
    {block.author?.trim() && (
      <p className="mt-1.5 text-[11.5px] font-semibold" style={{ color: subInk }}>— {block.author}</p>
    )}
  </div>
);

// ============ SOCIAL ============

const SocialBlock = ({ block, isLight, interactive }) => {
  const items = (block.items || []).filter((s) => s?.value?.trim());
  if (items.length === 0) return null;
  const makeIcon = (s, i) => {
    const platform = getSocialPlatform(s.platform);
    if (!platform) return null;
    const { Icon } = platform;
    const el = (
      <span className="w-10 h-10 rounded-full grid place-items-center shrink-0 transition-transform hover:scale-110"
        style={{ background: isLight ? "#fff" : "rgba(255,255,255,.08)", color: isLight ? platform.color : "#fff", boxShadow: `inset 0 0 0 1px ${isLight ? "rgba(0,0,0,.08)" : "rgba(255,255,255,.12)"}` }}>
        <Icon className="w-4.5 h-4.5" strokeWidth={2.25} />
      </span>
    );
    if (!interactive) return <span key={i}>{el}</span>;
    return (
      <a key={i} href={platform.urlFor(s.value)} target="_blank" rel="noopener noreferrer">{el}</a>
    );
  };
  return <div className="flex justify-center gap-2.5 flex-wrap">{items.map(makeIcon)}</div>;
};

// ============ MAP ============

const MapBlock = ({ block, radius, isLight, interactive }) => {
  const url = (block.url || "").trim();
  if (!url) return null;
  const src = isMapsEmbedUrl(url) ? url : `https://www.google.com/maps?q=${encodeURIComponent(url)}&output=embed`;
  const title = (block.title || "").trim();

  if (!interactive) {
    return (
      <div className="w-full grid place-items-center" style={{ height: 160, borderRadius: radius, background: `${isLight ? "#e9e9e6" : "rgba(255,255,255,.06)"}`, border: `1px solid ${isLight ? "rgba(0,0,0,.08)" : "rgba(255,255,255,.12)"}` }}>
        <span className="flex items-center gap-2 opacity-60"><MapPin className="w-4 h-4" /> {title || "Peta lokasi"}</span>
      </div>
    );
  }
  return (
    <div style={{ borderRadius: radius, overflow: "hidden", border: `1px solid ${isLight ? "rgba(0,0,0,.08)" : "rgba(255,255,255,.12)"}` }}>
      <iframe src={src} title={title || "Peta"} className="w-full" style={{ height: 200, border: 0 }} loading="lazy" referrerPolicy="no-referrer-when-downgrade" />
    </div>
  );
};

// ============ FAQ ============

const FaqBlock = ({ block, ink, subInk, isLight }) => {
  const items = (block.items || []).filter((f) => f?.question?.trim());
  if (items.length === 0) return null;
  return (
    <Accordion type="single" collapsible className="w-full">
      {items.map((f, i) => (
        <AccordionItem key={i} value={`item-${i}`} className="border-0 mb-2"
          style={{ background: isLight ? "#ffffff" : "rgba(255,255,255,.06)", borderRadius: 10, border: `1px solid ${isLight ? "rgba(0,0,0,.08)" : "rgba(255,255,255,.12)"}`, padding: "2px 12px" }}>
          <AccordionTrigger className="hover:no-underline py-3" style={{ fontSize: 13.5, fontWeight: 700, color: ink }}>
            {f.question}
          </AccordionTrigger>
          <AccordionContent className="pb-3" style={{ fontSize: 12.5, color: subInk, lineHeight: 1.6 }}>
            {f.answer}
          </AccordionContent>
        </AccordionItem>
      ))}
    </Accordion>
  );
};

// ============ NEWSLETTER ============

const NewsletterBlock = ({ block, color, radius, ink, subInk, isLight, interactive, microsite }) => {
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState("idle"); // idle | loading | done
  const title = block.title || "Berlangganan";
  const description = block.description || "";

  const submit = async (e) => {
    e.preventDefault();
    if (!interactive || !email.trim() || !microsite?.slug) return;
    setStatus("loading");
    try {
      await axios.post(`${API}/m/${microsite.slug}/subscribe`, { email: email.trim() });
      setStatus("done");
      setEmail("");
    } catch {
      setStatus("idle");
    }
  };

  return (
    <div style={{ borderRadius: radius, background: isLight ? "#ffffff" : "rgba(255,255,255,.06)", border: `1px solid ${isLight ? "rgba(0,0,0,.08)" : "rgba(255,255,255,.12)"}`, padding: "16px 14px" }}>
      <div className="text-center mb-2.5">
        <p className="font-bold" style={{ fontSize: 14, color: ink }}>{title}</p>
        {description && <p className="mt-0.5" style={{ fontSize: 12, color: subInk }}>{description}</p>}
      </div>
      {status === "done" ? (
        <p className="flex items-center justify-center gap-1.5 py-2 text-center text-[12.5px] font-semibold" style={{ color: "#10b981" }}>
          <MailCheck className="w-4 h-4" /> Berhasil berlangganan!
        </p>
      ) : (
        <form onSubmit={submit} className="flex gap-2">
          <input
            type="email"
            required
            disabled={!interactive}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="Email Anda"
            className="flex-1 min-w-0 px-3 py-2 rounded-lg text-[13px] outline-none"
            style={{ background: isLight ? "#f3f3f1" : "rgba(255,255,255,.08)", color: ink, border: `1px solid ${isLight ? "rgba(0,0,0,.1)" : "rgba(255,255,255,.14)"}` }}
          />
          <button type="submit" disabled={!interactive || status === "loading"}
            className="px-3.5 py-2 rounded-lg text-[13px] font-bold shrink-0 transition-opacity hover:opacity-90 disabled:opacity-50"
            style={{ background: color, color: "#fff" }}>
            {status === "loading" ? <Loader2 className="w-4 h-4 animate-spin" /> : "Kirim"}
          </button>
        </form>
      )}
    </div>
  );
};

// ============ COUNTDOWN ============

const pad = (n) => String(n).padStart(2, "0");

const CountdownBlock = ({ block, color, ink, subInk, isLight, interactive }) => {
  const target = block.deadline ? new Date(block.deadline).getTime() : null;
  const calc = () => {
    if (!target) return null;
    const diff = target - Date.now();
    if (diff <= 0) return null;
    return {
      d: Math.floor(diff / 86400000),
      h: Math.floor((diff % 86400000) / 3600000),
      m: Math.floor((diff % 3600000) / 60000),
      s: Math.floor((diff % 60000) / 1000),
    };
  };
  const [left, setLeft] = useState(calc);

  useEffect(() => {
    setLeft(calc());
    if (!interactive || !target) return;
    const t = setInterval(() => setLeft(calc()), 1000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [target, interactive]);

  const cells = left
    ? [{ v: left.d, l: "Hari" }, { v: left.h, l: "Jam" }, { v: left.m, l: "Menit" }, { v: left.s, l: "Detik" }]
    : null;

  return (
    <div className="text-center">
      {block.title?.trim() && <p className="font-bold mb-2.5" style={{ fontSize: 14, color: ink }}>{block.title}</p>}
      {cells ? (
        <div className="flex justify-center gap-2">
          {cells.map((c, i) => (
            <div key={i} className="flex flex-col items-center justify-center rounded-lg" style={{ width: 56, height: 56, background: isLight ? "#ffffff" : "rgba(255,255,255,.08)", border: `1px solid ${isLight ? "rgba(0,0,0,.08)" : "rgba(255,255,255,.12)"}` }}>
              <span className="font-bold tabular-nums" style={{ fontSize: 17, color: ink }}>{pad(c.v)}</span>
              <span className="text-[9px] uppercase tracking-wide" style={{ color: subInk }}>{c.l}</span>
            </div>
          ))}
        </div>
      ) : (
        <p className="text-[12.5px] font-semibold" style={{ color: color }}>{target ? "Waktunya tiba! 🎉" : "Atur tanggal & waktu countdown"}</p>
      )}
    </div>
  );
};

// ============ RENDERER UTAMA BLOK ============

// ctx: { color, radius, ink, subInk, isLight, interactive, onLinkClick, microsite }
export const BlockRenderer = ({ block, index, ctx }) => {
  switch (block.type) {
    case "audio": return <AudioCard block={block} index={index} color={ctx.color} radius={ctx.radius} ink={ctx.ink} subInk={ctx.subInk} isLight={ctx.isLight} interactive={ctx.interactive} onLinkClick={ctx.onLinkClick} />;
    case "image": return <ImageBlock block={block} index={index} radius={ctx.radius} ink={ctx.ink} subInk={ctx.subInk} isLight={ctx.isLight} interactive={ctx.interactive} onLinkClick={ctx.onLinkClick} />;
    case "gallery": return <GalleryBlock block={block} radius={ctx.radius} isLight={ctx.isLight} />;
    case "text": return <TextBlock block={block} ink={ctx.ink} />;
    case "heading": return <HeadingBlock block={block} ink={ctx.ink} />;
    case "divider": return <DividerBlock block={block} ink={ctx.ink} subInk={ctx.subInk} isLight={ctx.isLight} />;
    case "social": return <SocialBlock block={block} isLight={ctx.isLight} interactive={ctx.interactive} />;
    case "map": return <MapBlock block={block} radius={ctx.radius} isLight={ctx.isLight} interactive={ctx.interactive} />;
    case "faq": return <FaqBlock block={block} ink={ctx.ink} subInk={ctx.subInk} isLight={ctx.isLight} />;
    case "newsletter": return <NewsletterBlock block={block} color={ctx.color} radius={ctx.radius} ink={ctx.ink} subInk={ctx.subInk} isLight={ctx.isLight} interactive={ctx.interactive} microsite={ctx.microsite} />;
    case "countdown": return <CountdownBlock block={block} color={ctx.color} ink={ctx.ink} subInk={ctx.subInk} isLight={ctx.isLight} interactive={ctx.interactive} />;
    case "quote": return <QuoteBlock block={block} color={ctx.color} ink={ctx.ink} subInk={ctx.subInk} isLight={ctx.isLight} />;
    default: return null;
  }
};

export { SOCIAL_PLATFORMS };
